import { useCallback, useState } from "react";
import {
  FormControl,
  InputLabel,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import HelpIcon from "@mui/icons-material/HelpOutline";
import WarningIcon from "@mui/icons-material/WarningAmberOutlined";
import { GameMode, allRegions } from "./CoQuery";
import { Action, State } from "./CoState";
import { make as Tooltip } from "common/src/components/Tooltip.bs";
import { sendMsg } from "common/src/SnackbarService.bs";

import { allPatches, statsSet } from "common/src/Config";
import { render as renderPatch } from "common/src/Patch.bs";
import * as T from "common/src/bindings/Translation2";
import Check from "@mui/icons-material/Check";
import * as Sd from "common/src/tft-static/Sd";
import UnitImage from "common/src/tft-static/UnitImage";

type Props = {
  state: State;
  dispatch: (action: Action) => void;
};

type RootProps = {
  state: State;
  dispatch: (action: Action) => void;
  exaltedGroups: any;
};

const parseLastRound = (d1: string, d2: string) => {
  const s = Number(d1);
  const r = Number(d2);

  return (s - 2) * 7 + r + 4;
};

const parseRangeInput = (input: string): string | null => {
  const match = input.match(/(.*?)(\d+)(.*)/);
  if (match) {
    const prefix = match[1];
    const main = Number(match[2].replace("-", ""));
    const suffix = match[3];

    if (prefix !== "" && suffix !== "") {
      return null;
    } else if (prefix === "" && suffix === "") {
      return `${main}`;
    } else {
      const isPrefix = suffix === "";
      const modifier = suffix === "" ? prefix : suffix;
      if (
        modifier === "+" ||
        (modifier === ">=" && isPrefix) ||
        (modifier === "<=" && !isPrefix)
      ) {
        return `+${main}`;
      } else if (
        modifier === "-" ||
        (modifier === "<=" && isPrefix) ||
        (modifier === ">=" && !isPrefix)
      ) {
        return `-${main}`;
      } else if (
        (modifier === "<" && isPrefix) ||
        (modifier === ">" && !isPrefix)
      ) {
        return `-${main - 1}`;
      } else if (
        (modifier === ">" && isPrefix) ||
        (modifier === "<" && !isPrefix)
      ) {
        return `+${main + 1}`;
      } else {
        return null;
      }
    }
  } else {
    return null;
  }
};

const renderInitIc = (initIc: string) => {
  let prefix = "";
  let icNum;
  if (initIc[0] === "+") {
    prefix = ">";
    icNum = Number(initIc.slice(1)) - 1;
  } else if (initIc[0] === "-") {
    prefix = "<";
    icNum = Number(initIc.slice(1)) + 1;
  } else {
    icNum = Number(initIc);
  }

  return `${prefix}${icNum}`;
};

const IcFilter = ({ state, dispatch }: Props) => {
  const [itemCountInput, setItemCountInput] = useState(
    state.itemCount ? renderInitIc(state.itemCount) : "",
  );

  const updateIc = useCallback(() => {
    const match = itemCountInput.match(/(.*?)(\d+)(.*)/);
    if (match) {
      const prefix = match[1];
      const main = match[2].replace("-", "");
      const suffix = match[3];

      const ic = Number(main);

      if (!(ic > 0 && ic < 100)) {
        sendMsg("Invalid item count number, the filter will be ignored.");
        dispatch(["set_item_count", null]);
        return;
      }

      if (prefix !== "" && suffix !== "") {
        sendMsg(
          "Both prefix and suffix are present in item count filter, the filter will be ignored.",
        );
        dispatch(["set_item_count", null]);
      } else if (prefix === "" && suffix === "") {
        dispatch(["set_item_count", `${ic}`]);
      } else {
        const isPrefix = suffix === "";
        const modifier = suffix === "" ? prefix : suffix;
        const round = Number(main);
        if (
          modifier === "+" ||
          (modifier === ">=" && isPrefix) ||
          (modifier === "<=" && !isPrefix)
        ) {
          dispatch(["set_item_count", `+${round}`]);
        } else if (
          modifier === "-" ||
          (modifier === "<=" && isPrefix) ||
          (modifier === ">=" && !isPrefix)
        ) {
          dispatch(["set_item_count", `-${round}`]);
        } else if (
          (modifier === "<" && isPrefix) ||
          (modifier === ">" && !isPrefix)
        ) {
          dispatch(["set_item_count", `-${round - 1}`]);
        } else if (
          (modifier === ">" && isPrefix) ||
          (modifier === "<" && !isPrefix)
        ) {
          dispatch(["set_item_count", `+${round + 1}`]);
        } else {
          sendMsg(
            "Invalid item count prefix or suffix, the filter will be ignored.",
          );
          dispatch(["set_item_count", null]);
        }
      }
    } else {
      dispatch(["set_item_count", null]);
    }
  }, [itemCountInput]);

  return (
    <div className="flex gap-1 items-end">
      <TextField
        classes={{ root: "w-[120px]" }}
        variant="standard"
        value={itemCountInput}
        onChange={(e) => {
          const v = e.target.value;
          setItemCountInput((_) => v);
        }}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            updateIc();
          }
        }}
        onBlur={() => updateIc()}
        label={T.common("Item Count")}
      />
      <Tooltip
        title={
          <div className="flex flex-col gap-2">
            <div>
              Filters are based on total completed items used on player's board.
              Any uncombined item components, items generated by Thief's Gloves,
              or items generated by Gadgeteen are ignored.
            </div>
            <div className="pt-1">Example expressions:</div>
            <div>
              {`"6+" or "+6" or ">=6" or "6<= "will filter all comps from games where player's total item count is 6 or more.`}
            </div>
            <div>
              {`"6-" or "-6" or "<=6" or "6>=" will filter all comps from games where player's total item count is 6 or less.`}
            </div>
            <div>
              {`"<6" or "6>" will filter all comps from games where player's total item count is less than 6 (5 items or less).`}
            </div>
            <div>
              {`">6 or "6<" will filter all comps from games where player's total item count is more than 6 (7 items or more).`}
            </div>

            <div className="flex items-center gap-[6px] pt-1">
              <WarningIcon fontSize="small" />
              This filter can introduce heavily biases to data, use responsibly.
              Any and all LP lost from using data irresponsibly is
              non-refundable.
            </div>
          </div>
        }
      >
        <HelpIcon className="mb-[5px]" fontSize="small" />
      </Tooltip>
    </div>
  );
};

const renderInitLr = (initLr: string) => {
  let prefix = "";
  let lrNum;
  if (initLr[0] === "+") {
    prefix = ">";
    lrNum = Number(initLr.slice(1)) - 1;
  } else if (initLr[0] === "-") {
    prefix = "<";
    lrNum = Number(initLr.slice(1)) + 1;
  } else {
    lrNum = Number(initLr);
  }

  const stage = Math.floor((lrNum - 4) / 7) + 2;
  const round = (lrNum - 4) % 7;

  return `${prefix}${stage}-${round}`;
};

const LrFilter = ({ state, dispatch }: Props) => {
  const [lastRoundInput, setLastRoundInput] = useState(
    state.lastRound ? renderInitLr(state.lastRound) : "",
  );

  const updateLr = useCallback(() => {
    const match = lastRoundInput.match(/(.*?)(\d-?\d)(.*)/);
    if (match) {
      const prefix = match[1];
      const main = match[2].replace("-", "");
      const suffix = match[3];

      if (main.length !== 2) {
        dispatch(["set_last_round", null]);
        sendMsg(
          "Last round filter should contain 2 digits, the filter will be ignored.",
        );
        return;
      }

      const round = parseLastRound(main[0], main[1]);

      if (round < 10) {
        sendMsg("Invalid round number, the filter will be ignored.");
        dispatch(["set_last_round", null]);
        return;
      }

      if (prefix !== "" && suffix !== "") {
        sendMsg(
          "Both prefix and suffix are present in last round filter, the filter will be ignored.",
        );
        dispatch(["set_last_round", null]);
      } else if (prefix === "" && suffix === "") {
        dispatch(["set_last_round", `${round}`]);
      } else {
        const isPrefix = suffix === "";
        const modifier = suffix === "" ? prefix : suffix;
        if (
          modifier === "+" ||
          (modifier === ">=" && isPrefix) ||
          (modifier === "<=" && !isPrefix)
        ) {
          dispatch(["set_last_round", `+${round}`]);
        } else if (
          modifier === "-" ||
          (modifier === "<=" && isPrefix) ||
          (modifier === ">=" && !isPrefix)
        ) {
          dispatch(["set_last_round", `-${round}`]);
        } else if (
          (modifier === "<" && isPrefix) ||
          (modifier === ">" && !isPrefix)
        ) {
          dispatch(["set_last_round", `-${round - 1}`]);
        } else if (
          (modifier === ">" && isPrefix) ||
          (modifier === "<" && !isPrefix)
        ) {
          dispatch(["set_last_round", `+${round + 1}`]);
        } else {
          dispatch(["set_last_round", null]);
          sendMsg(
            "Invalid last round prefix or suffix, the filter will be ignored.",
          );
        }
      }
    } else {
      dispatch(["set_last_round", null]);
    }
  }, [lastRoundInput]);

  return (
    <div className="flex gap-1 items-end">
      <TextField
        classes={{ root: "w-[120px]" }}
        variant="standard"
        value={lastRoundInput}
        onChange={(e) => {
          const v = e.target.value;
          setLastRoundInput((_) => v);
        }}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            updateLr();
          }
        }}
        onBlur={() => updateLr()}
        label={T.common("Last Round")}
      />
      <Tooltip
        title={
          <div className="flex flex-col gap-2">
            <div>
              Filters data on the last round where player either got eliminated
              or won the game. Last Round can be entered as "4-6" or just "46",
              where the dash is optional.
            </div>
            <div>
              {`Supports additional filtering using  "+", "-", ">", "<", ">=", "<=" expression symbols. Expression symbols can be before or after the round number.`}
            </div>
            <div className="pt-1">Example expressions:</div>
            <div>
              {`"5-5+" or "55+" or "+55" or ">=55" or "55<=" will filter all comps from games where player's last round is 5-5 or later.`}{" "}
            </div>
            <div>
              {`"5-5-" or "55-" or "-55" or "<=55" or "55>=" will filter all comps from games where player's last round is 5-5 or earlier.`}{" "}
            </div>
            <div>
              {`"<55" or "55>" will filter all comps from games where player's last round is earlier than 5-5 (5-4 being the last round or earlier).`}
            </div>
            <div>
              {`">55" or "55<" will filter all comps from games where player's last round is later than 5-5 (5-6 being the last round or later).`}
            </div>
            <div className="flex items-center gap-[6px] pt-1">
              <WarningIcon fontSize="small" />
              This filter can introduce heavily biases to data, use responsibly.
              Any and all LP lost from using data irresponsibly is
              non-refundable.
            </div>
          </div>
        }
      >
        <HelpIcon className="mb-[5px]" fontSize="small" />
      </Tooltip>
    </div>
  );
};

type MultiSelectProps = {
  value: Array<string>;
  onChange: (vs: Array<string>) => void;
  options: Array<[string, string]>;
  labelId: string;
  label: string;
  disabled?: boolean;
};

const MultiSelect = ({
  value,
  onChange,
  options,
  labelId,
  label,
  disabled,
}: MultiSelectProps) => {
  return (
    <FormControl variant="standard" sx={{ width: 120 }}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        disabled={disabled}
        size="small"
        variant="standard"
        multiple
        value={value}
        labelId={labelId}
        classes={{ select: "pt-1" }}
        onChange={(e) => onChange(e.target.value as Array<string>)}
        renderValue={(selected) => {
          return selected
            .map((sv) => options.find((o) => o[0] == sv)![1])
            .join(", ");
        }}
      >
        {options.map(([key, label]) => (
          <MenuItem
            key={key}
            value={key}
            classes={{ root: "flex justify-between gap-3" }}
          >
            {label}
            {value.includes(key) ? <Check fontSize="small" /> : null}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const CoAdvancedFilters = ({ state, dispatch, exaltedGroups }: RootProps) => {
  return (
    <div className="bg-bg p-4 mt-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 items-end gap-4 pt-2">
        <FormControl variant="standard" sx={{ width: 120 }}>
          <InputLabel id="rank-select">{T.common("Rank")}</InputLabel>
          <Select
            labelId="rank-select"
            size="small"
            variant="standard"
            value={state.rankGroup}
            onChange={(e) =>
              dispatch(["set_rank_group", Number(e.target.value)])
            }
          >
            <MenuItem value={5}>
              {T.common(state.gameMode === "hr" ? "6k+" : "Challenger")}
            </MenuItem>
            <MenuItem value={4}>
              {T.common(state.gameMode === "hr" ? "Hyper+" : "GM+")}
            </MenuItem>
            <MenuItem value={0}>
              {T.common(state.gameMode === "hr" ? "Purple+" : "Master+")}
            </MenuItem>
            <MenuItem value={1}>
              {T.common(state.gameMode === "hr" ? "Blue+" : "Diamond+")}
            </MenuItem>
            <MenuItem value={2}>
              {T.common(state.gameMode === "hr" ? "Green+" : "Emerald+")}
            </MenuItem>
            <MenuItem value={3}>
              {T.common(state.gameMode === "hr" ? "Gray+" : "Platinum+")}
            </MenuItem>
            <MenuItem value={6}>
              {T.common(state.gameMode === "hr" ? "Gray+" : "Gold+")}
            </MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="standard" sx={{ width: 120 }}>
          <InputLabel id="patch-select">{T.common("Patch")}</InputLabel>
          <Select
            labelId="patch-select"
            size="small"
            variant="standard"
            value={state.patch}
            onChange={(e) => dispatch(["set_patch", Number(e.target.value)])}
          >
            {(state.availablePatches.length
              ? state.availablePatches
              : allPatches.slice(0, 3)
            ).map((patch) => (
              <MenuItem key={patch} value={patch}>
                {renderPatch(patch)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* <div className="flex items-end gap-1">
          <MultiSelect
            value={state.portals.map(String)}
            onChange={(portals) =>
              dispatch(["select_portal", portals.map(Number)])
            }
            label={T.common("Portal")}
            labelId="portal-select"
            options={[
              // ["1", T.common("TFT_Portals_Headliner_Superstar")],
              ["3", T.common("TFT_Portals_Items_ArtifactAnvil")],
              ["4", T.common("TFT_Portals_Items_RadiantItem")],
              ["5", T.common("TFT_Portals_Unique_Spatula")],
              ["6", T.common("TFT_Portals_Items_TacticiansCrown")],
              ["7", T.common("TFT_Portals_Items_SupportAnvil")],
              ["8", T.common("TFT_Portals_Combat_WanderingTrainers")],
              ["0", "Other"],
            ]}
          />
          <Tooltip
            title={
              <div className="flex flex-col gap-2">
                <div>
                  Portals are not present in the Riot's API data but are
                  determined from special features of the game in players'
                  units/traits/items. For example if everyone has a Tactician's
                  Crowns, that means the game probably took place in a portal
                  that grants everyone a Tactician's Crown.
                </div>
                <div>
                  This way of determining portals is not perfectly accurate but
                  should produce statistically relevant results.
                </div>
              </div>
            }
          >
            <HelpIcon className="mb-[5px]" fontSize="small" />
          </Tooltip>
        </div> */}
        <div className="flex items-end gap-1">
          <MultiSelect
            value={state.regions}
            onChange={(regions) => dispatch(["select_region", regions])}
            label={T.common("Region")}
            labelId="region-select"
            // Need to make copy here to not mess up encoding
            options={allRegions
              .slice(0)
              .filter(([id]) => id != "ph2" && id != "th2")
              .sort((v1, v2) => v1[1].localeCompare(v2[1]))}
          />
        </div>
        {/* </div>
      <div className="flex items-end gap-4 pt-6 mt-[-12px]"> */}
        {/* <TextField
          classes={{ root: "w-[120px]" }}
          variant="standard"
          value={levelInput}
          onChange={(e) => {
            const v = e.target.value;
            setLevelInput((_) => v);
          }}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              updateLevel();
            }
          }}
          onBlur={() => updateLevel()}
          label="Level"
        /> */}
        {/*<div>
          <FormControl variant="standard" sx={{ width: 140 }}>
            <InputLabel id="game-type-select">Exalted</InputLabel>
            <Select
              labelId="game-type-select"
              size="small"
              variant="standard"
              value={state.gameType}
              onChange={(e) =>
                dispatch(["set_game_type", Number(e.target.value)])
              }
            >
              <MenuItem value={0}>{T.common("None")}</MenuItem>
              {Array.from({ length: 60 }).map((_, idx) => (
                <MenuItem value={idx + 1}>
                  <div className="flex gap-1">
                    {exaltedGroups[idx].map((unitId: string) => (
                      <UnitImage unitId={unitId} size={20} />
                    ))}
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>*/}
        <FormControl variant="standard" sx={{ width: 120 }}>
          <InputLabel id="rank-select">{T.common("Game Mode")}</InputLabel>
          <Select
            labelId="rank-select"
            size="small"
            variant="standard"
            value={state.gameMode}
            onChange={(e) =>
              dispatch(["set_gamemode", e.target.value as GameMode])
            }
          >
            <MenuItem value="ranked">{T.common("Ranked")}</MenuItem>
            <MenuItem value="hr">{T.common("Hyperroll")}</MenuItem>
            <MenuItem value="duo">{T.common("Double Up")}</MenuItem>
            {/* <MenuItem value="pbe">{T.common("PBE")}</MenuItem> */}
            <MenuItem value="temp">
              {T.common("Set #").replace("#", "10") + " Revival"}
            </MenuItem>
          </Select>
        </FormControl>
        <MultiSelect
          value={state.levels.map(String)}
          onChange={(levels) => dispatch(["select_level", levels.map(Number)])}
          label={T.common("Level")}
          labelId="level-select"
          options={[
            ["5", "5"],
            ["6", "6"],
            ["7", "7"],
            ["8", "8"],
            ["9", "9"],
            ["10", "10"],
          ]}
        />
        <IcFilter state={state} dispatch={dispatch} />
        <LrFilter state={state} dispatch={dispatch} />
        <FormControl variant="standard" sx={{ width: 120 }}>
          <InputLabel id="date-select">{T.common("Date")}</InputLabel>
          <Select
            labelId="date-select"
            size="small"
            variant="standard"
            value={String(state.date)}
            onChange={(e) => dispatch(["set_date", Number(e.target.value)])}
          >
            <MenuItem value="-1">{T.common("All")}</MenuItem>
            <MenuItem value="0">{T.common("Last day")}</MenuItem>
            <MenuItem value="1">{T.common("Last 2 days")}</MenuItem>
            <MenuItem value="2">{T.common("Last 3 days")}</MenuItem>
            <MenuItem value="3">{T.common("Last 4 days")}</MenuItem>
            <MenuItem value="4">{T.common("Last 5 days")}</MenuItem>
            <MenuItem value="5">{T.common("Last 6 days")}</MenuItem>
            <MenuItem value="6">{T.common("Last 7 days")}</MenuItem>
            <MenuItem value="7">{T.common("Last 10 days")}</MenuItem>
          </Select>
        </FormControl>
      </div>
    </div>
  );
};

export default CoAdvancedFilters;
